import { Title } from '@solidjs/meta'
import { type Component, createSignal } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Menu from '~/components/Menu'
import Table from '~/components/Table'
import type { MainColorsType } from '~/types/components'

interface Product {
  id: number
  name: string
  category: string
  image: string
  createdAt: Date
  stock: number
  price: number
  status: 'published' | 'draft'
}

const PRODUCT_NAMES = [
  ['Urban Explorer Sneakers', 'Accesorios'],
  ['Classic Leather Loafers', 'Zapatos'],
  ['Mountain Trekking Boots', 'Ropa'],
  ['Elegance Stiletto Heels', 'Zapatos'],
  ['Comfy Running Shoes', 'Ropa'],
  ['Vintage Canvas Hi-Tops', 'Accesorios'],
  ['Performance Trail Runners', 'Ropa'],
  ['Minimalist Slip-Ons', 'Zapatos'],
  ['Premium Suede Derbies', 'Zapatos'],
  ['All-Weather Rain Boots', 'Ropa']
] as const

/** Dataset determinista (sin red) para el showcase. */
const buildProducts = (): Product[] =>
  Array.from({ length: 20 }, (_, i) => {
    const [name, category] = PRODUCT_NAMES[i % PRODUCT_NAMES.length]
    const stock = [0, 72, 10, 72, 10, 34, 0, 56, 10, 88][i % 10]
    return {
      id: i + 1,
      name: i < 10 ? name : `${name} ${Math.floor(i / 10) + 1}`,
      category,
      image: `https://picsum.photos/seed/fj-product-${i + 1}/80/80`,
      createdAt: new Date(2026, 5, 19 - i),
      stock,
      price: Math.round((52 + i * 3.17) * 100) / 100,
      status: i % 3 === 0 ? 'draft' : 'published'
    }
  })

const fmtDate = (d: Date) => d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })
const fmtTime = (d: Date) => d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })

/** Color + etiqueta de la barra de stock. */
const stockInfo = (n: number): { color: MainColorsType; label: string; value: number } => {
  if (n === 0) return { color: 'danger', label: 'agotado', value: 4 }
  if (n <= 10) return { color: 'warning', label: `${n} bajo stock`, value: n }
  return { color: 'success', label: `${n} en stock`, value: n }
}

const TablePage: Component = () => {
  const [products, setProducts] = createSignal<Product[]>(buildProducts())

  const removeProduct = (id: number) => setProducts(prev => prev.filter(p => p.id !== id))

  return (
    <>
      <Title>Table - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Table</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Tabla de datos lista para producción: tabs de filtro con contador, búsqueda integrada, selección masiva, ordenamiento, paginación y celdas helper (media, progreso y estado) para armar
            vistas tipo dashboard en minutos.
          </p>
        </div>

        <div class="space-y-12">
          {/* Showcase principal estilo Minimal UI */}
          <Table
            data={products()}
            keyField="id"
            selectable
            selectableColor="primary"
            showDense
            searchable
            searchPlaceholder="Buscar producto..."
            perPage={5}
            perPageOptions={[5, 10, 20]}
            showFilters
            filterField="status"
            filterOptions={[
              { label: 'Todos', value: 'all', color: 'secondary' },
              { label: 'Publicados', value: 'published', color: 'success' },
              { label: 'Borradores', value: 'draft', color: 'default' }
            ]}
            toolbarActions={
              <>
                <Button variant="text" size="sm" color="default" icon="material-symbols-light:filter-list">
                  Filtros
                </Button>
                <Button variant="text" size="sm" color="default" icon="material-symbols-light:upload">
                  Exportar
                </Button>
              </>
            }
            onDeleteSelected={() => alert('Eliminar seleccionados')}
            selectableAction={selected => (
              <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:delete-outline">
                Eliminar ({selected.size})
              </Button>
            )}
            columns={[
              {
                header: 'Producto',
                sortable: true,
                sortKey: 'name',
                accessor: (item: Product) => <Table.Media src={item.image} title={item.name} subtitle={item.category} />
              },
              {
                header: 'Creado',
                sortable: true,
                sortKey: 'createdAt',
                accessor: (item: Product) => (
                  <div>
                    <div class="text-foreground">{fmtDate(item.createdAt)}</div>
                    <div class="text-xs text-foreground-subtle">{fmtTime(item.createdAt)}</div>
                  </div>
                )
              },
              {
                header: 'Stock',
                sortable: true,
                sortKey: 'stock',
                accessor: (item: Product) => {
                  const info = stockInfo(item.stock)
                  return <Table.Progress value={info.value} color={info.color} label={info.label} />
                }
              },
              {
                header: 'Precio',
                sortable: true,
                sortKey: 'price',
                class: 'text-right',
                cellClass: 'text-right font-medium text-foreground',
                accessor: (item: Product) => `$${item.price.toFixed(2)}`
              },
              {
                header: 'Estado',
                sortable: true,
                sortKey: 'status',
                accessor: (item: Product) => <Table.Status color={item.status === 'published' ? 'success' : 'default'}>{item.status === 'published' ? 'Publicado' : 'Borrador'}</Table.Status>
              },
              {
                header: '',
                class: 'w-0',
                accessor: (item: Product) => (
                  <Menu
                    trigger={<Button icon="material-symbols-light:more-horiz" variant="text" size="sm" color="default" aria-label="Acciones" />}
                    items={[
                      { label: 'Editar', icon: 'material-symbols-light:edit-outline', onClick: () => alert(`Editar ${item.name}`) },
                      { label: 'Eliminar', icon: 'material-symbols-light:delete-outline', color: 'danger', onClick: () => removeProduct(item.id) }
                    ]}
                  />
                )
              }
            ]}
          />

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades destacadas del componente Table." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">searchable</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 italic">Activa el buscador integrado en la toolbar (filtrado en cliente o vía onSearch).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">filterOptions / filterField</td>
                      <td class="px-6 py-4 italic text-xs">{'{label,value,count?,color?}[]'}</td>
                      <td class="px-6 py-4">Tabs de filtro con contador autocalculado y filtrado automático por campo.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">toolbarActions</td>
                      <td class="px-6 py-4 italic text-xs">JSX.Element</td>
                      <td class="px-6 py-4 italic">Botones a la derecha de la toolbar (Columnas, Exportar…).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">selectable / onDeleteSelected</td>
                      <td class="px-6 py-4 italic text-xs">boolean / () =&gt; void</td>
                      <td class="px-6 py-4">Selección masiva con banner de acciones y botón de papelera.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">Table.Media / Progress / Status</td>
                      <td class="px-6 py-4 italic text-xs">Component</td>
                      <td class="px-6 py-4 italic">Celdas helper: media (thumb+título), barra de progreso y píldora de estado.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default TablePage
